import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Chip, CircularProgress, Alert,
  FormControl, InputLabel, Select, MenuItem, LinearProgress, Tooltip,
  IconButton, Collapse, List, ListItem, ListItemText, ListItemButton, Divider,
  Checkbox,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BoltIcon from '@mui/icons-material/Bolt';
import CategoryIcon from '@mui/icons-material/Category';
import { useTranslation } from 'react-i18next';
import { waveService, SmartWaveGroup, SmartGroupingMode } from '../../services/waveService';
import { orderService } from '../../services/orderService';
import WmsStationHeader from '../../shared/WmsStationHeader';
import WmsListSection from '../../shared/WmsListSection';

const WMS_WAVE_STATUS_EVENT = 'wms-wave-status-changed';
const WMS_WAVE_STATUS_STORAGE_KEY = 'wms-wave-status-ts';

export interface PoolWaveInfo {
  id: string;
  waveNumber: string;
  waveType: string;
  status: string;
  totalOrders: number;
  totalItems: number;
  pickedItems: number;
  startedAt?: string;
}

type PoolOrderType = 'Single' | 'Multi';

// Akilli gruplama modlarina ek olarak "Manual": toplayici siparisleri kendisi secer.
type GroupingChoice = SmartGroupingMode | 'Manual';

interface PoolPendingOrder {
  id: string;
  orderNumber: string;
  customerName: string;
  itemCount: number;
  priority: number;
}

interface PickingPoolProps {
  personnelName: string;
  personnelId?: string;
  warehouseId: string;
  /** Ust ekrandan gelen tip (Tekli/Coklu) ? burada degistirilmez. */
  orderType: PoolOrderType;
  /** MarketplaceSource (hepsiburada/trendyol/website). */
  channel?: string;
  channelLabel?: string;
  onBack: () => void;
  onWaveStarted: (wave: PoolWaveInfo) => void;
}

const GROUPING_OPTIONS: { value: GroupingChoice; labelKey: string }[] = [
  { value: 'Manual', labelKey: 'groupingManual' },
  { value: 'Auto', labelKey: 'groupingAuto' },
  { value: 'Category', labelKey: 'groupingCategory' },
  { value: 'Location', labelKey: 'groupingLocation' },
  { value: 'Product', labelKey: 'groupingProduct' },
  { value: 'VariantFamily', labelKey: 'groupingVariantFamily' },
];

const cohesionColor = (score: number): 'success' | 'warning' | 'error' => {
  if (score >= 70) return 'success';
  if (score >= 40) return 'warning';
  return 'error';
};

const PickingPool: React.FC<PickingPoolProps> = ({
  personnelName, personnelId, warehouseId, orderType, channel, channelLabel,
  onBack, onWaveStarted,
}) => {
  const { t } = useTranslation('warehouse');
  // Varsayilan: toplayici siparisleri kendisi secer.
  const [groupingMode, setGroupingMode] = useState<GroupingChoice>('Manual');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [groups, setGroups] = useState<SmartWaveGroup[]>([]);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [starting, setStarting] = useState<number | null>(null);

  const [manualOrders, setManualOrders] = useState<PoolPendingOrder[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [startingManual, setStartingManual] = useState(false);

  const isManual = groupingMode === 'Manual';

  const loadGroups = useCallback(async () => {
    if (!warehouseId) return;
    setLoading(true);
    setError('');
    try {
      if (groupingMode === 'Manual') {
        const res = await orderService.getWaveCandidates(warehouseId, orderType, 200, channel);
        const data = res.data?.data ?? res.data ?? [];
        const list: PoolPendingOrder[] = (Array.isArray(data) ? data : []).map((o: any) => ({
          id: o.id ?? o.Id,
          orderNumber: o.orderNumber ?? o.OrderNumber ?? '',
          customerName: o.customerName ?? o.CustomerName ?? '',
          itemCount: o.itemCount ?? o.ItemCount ?? 0,
          priority: o.priority ?? o.Priority ?? 0,
        }));
        setManualOrders(list);
        setSelectedIds(new Set());
        setGroups([]);
      } else {
        const res = await waveService.suggestSmartGrouping({
          WarehouseId: warehouseId,
          OrderTypeFilter: orderType,
          GroupingMode: groupingMode,
          MarketplaceSource: channel,
        });
        const data = res.data?.data || res.data;
        const grps: SmartWaveGroup[] = data?.groups || [];
        setGroups(grps);
        setManualOrders([]);
        setExpanded(grps.length === 1 ? new Set([grps[0].groupIndex]) : new Set());
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || (groupingMode === 'Manual'
        ? t('screens.picking.pool.loadErrorManual')
        : t('screens.picking.pool.loadErrorGroups')));
      setGroups([]);
      setManualOrders([]);
    }
    setLoading(false);
  }, [warehouseId, groupingMode, orderType, channel, t]);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds(prev => (
      prev.size === manualOrders.length ? new Set() : new Set(manualOrders.map(o => o.id))
    ));
  }, [manualOrders]);

  const toggleExpand = useCallback((idx: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  }, []);

  const startWaveFromOrders = useCallback(async (
    orderIds: string[], fallbackOrders: number, fallbackItems: number,
  ) => {
    const createRes = await waveService.create({
      WarehouseId: warehouseId,
      OrderIds: orderIds,
    });
    const waveId: string | undefined = createRes.data?.data?.id || createRes.data?.id;
    if (!waveId) throw new Error(t('screens.picking.pool.createFailed'));

    if (personnelId) {
      try {
        await waveService.assignPersonnelToWave({
          WaveId: waveId,
          PersonnelId: personnelId,
          PersonnelName: personnelName,
        });
      } catch { /* atama opsiyonel */ }
    }

    await waveService.start(waveId);

    window.dispatchEvent(new CustomEvent(WMS_WAVE_STATUS_EVENT, { detail: { warehouseId } }));
    localStorage.setItem(WMS_WAVE_STATUS_STORAGE_KEY, JSON.stringify({ ts: Date.now(), warehouseId }));

    const detRes = await waveService.getDetail(waveId);
    const d = detRes.data?.data || detRes.data || {};

    onWaveStarted({
      id: waveId,
      waveNumber: d.waveNumber || '',
      waveType: d.waveType || orderType,
      status: 'InProgress',
      totalOrders: d.totalOrders ?? fallbackOrders,
      totalItems: d.totalItems ?? fallbackItems,
      pickedItems: d.pickedItems ?? 0,
      startedAt: d.startedAt,
    });
  }, [warehouseId, personnelId, personnelName, onWaveStarted, t, orderType]);

  const handleStartGroup = useCallback(async (g: SmartWaveGroup) => {
    if (!warehouseId || starting !== null || startingManual) return;
    setStarting(g.groupIndex);
    setError('');
    try {
      await startWaveFromOrders(g.orders.map(o => o.id), g.totalOrders, g.totalItems);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('screens.picking.pool.startFailed'));
      setStarting(null);
    }
  }, [warehouseId, starting, startingManual, startWaveFromOrders, t]);

  const handleStartManual = useCallback(async () => {
    if (!warehouseId || startingManual || starting !== null) return;
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setStartingManual(true);
    setError('');
    try {
      const items = manualOrders
        .filter(o => selectedIds.has(o.id))
        .reduce((sum, o) => sum + (o.itemCount || 0), 0);
      await startWaveFromOrders(ids, ids.length, items);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('screens.picking.pool.startFailed'));
      setStartingManual(false);
    }
  }, [warehouseId, startingManual, starting, selectedIds, manualOrders, startWaveFromOrders, t]);

  const typeLabel = orderType === 'Multi'
    ? t('screens.picking.common.multi')
    : t('screens.picking.common.single');

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 880, mx: 'auto' }}>
      <WmsStationHeader
        title={channelLabel
          ? `${t('screens.picking.pool.title')} - ${channelLabel} - ${typeLabel}`
          : t('screens.picking.pool.title')}
        subtitle={t('screens.picking.pool.subtitleLocked')}
      />

      <Box
        sx={{
          display: 'flex',
          gap: { xs: 1, sm: 2 },
          alignItems: 'center',
          flexWrap: 'wrap',
          mt: { xs: 2, sm: 3 },
          mb: 2,
        }}
      >
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ color: 'text.secondary' }}>
          {t('screens.picking.pool.back')}
        </Button>
        <Chip
          label={typeLabel}
          color={orderType === 'Multi' ? 'secondary' : 'info'}
          size="small"
          sx={{ fontWeight: 700 }}
        />
        <FormControl
          size="small"
          sx={{
            flex: { xs: '1 1 140px', sm: '0 1 220px' },
            minWidth: { xs: 140, sm: 220 },
          }}
        >
          <InputLabel>{t('screens.picking.pool.groupingLabel')}</InputLabel>
          <Select
            label={t('screens.picking.pool.groupingLabel')}
            value={groupingMode}
            onChange={e => setGroupingMode(e.target.value as GroupingChoice)}
            disabled={loading || starting !== null || startingManual}
          >
            {GROUPING_OPTIONS.map(o => (
              <MenuItem key={o.value} value={o.value}>{t(`screens.picking.pool.${o.labelKey}`)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          onClick={loadGroups}
          disabled={loading || starting !== null || startingManual}
          sx={{
            flex: { xs: '1 1 auto', sm: '0 0 auto' },
            minWidth: { xs: 96, sm: 'auto' },
            whiteSpace: 'nowrap',
          }}
        >
          {loading ? <CircularProgress size={18} /> : t('screens.picking.pool.refresh')}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {isManual && (
        <WmsListSection
          title={t('screens.picking.pool.pendingOrders')}
          count={manualOrders.length}
          hasData={manualOrders.length > 0}
          emptyText={t('screens.picking.pool.emptyManual')}
        >
          {loading && manualOrders.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
          ) : (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Button
                  size="small"
                  onClick={toggleSelectAll}
                  disabled={manualOrders.length === 0 || startingManual}
                >
                  {selectedIds.size === manualOrders.length && manualOrders.length > 0
                    ? t('screens.picking.pool.deselect')
                    : t('screens.picking.pool.selectAll')}
                </Button>
                <Chip
                  label={t('screens.picking.pool.selectedChip', { count: selectedIds.size })}
                  size="small"
                  color={selectedIds.size > 0 ? 'primary' : 'default'}
                  variant="outlined"
                />
                <Box sx={{ flex: 1 }} />
                <Button
                  variant="contained"
                  startIcon={startingManual ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                  disabled={selectedIds.size === 0 || startingManual}
                  onClick={handleStartManual}
                  sx={{ minWidth: 200, height: 44 }}
                >
                  {t('screens.picking.pool.startSelected', { count: selectedIds.size })}
                </Button>
              </Box>
              <Divider sx={{ mb: 0.5 }} />
              <List dense sx={{ maxHeight: 440, overflow: 'auto' }}>
                {manualOrders.map(o => {
                  const checked = selectedIds.has(o.id);
                  return (
                    <ListItemButton
                      key={o.id}
                      onClick={() => toggleSelected(o.id)}
                      disabled={startingManual}
                      sx={{ borderRadius: 1, py: 0.25 }}
                    >
                      <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple size="small" />
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" fontWeight={600}>{o.orderNumber}</Typography>
                            <Typography variant="body2" color="text.secondary">{o.customerName}</Typography>
                            {o.priority >= 8 && (
                              <Tooltip title={t('screens.picking.pool.urgentTooltip')}>
                                <BoltIcon color="error" sx={{ fontSize: 16 }} />
                              </Tooltip>
                            )}
                          </Box>
                        }
                        secondary={t('screens.picking.common.itemsCount', { count: o.itemCount })}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          )}
        </WmsListSection>
      )}

      {!isManual && (
        <WmsListSection
          title={t('screens.picking.pool.generatedWaves')}
          count={groups.length}
          hasData={groups.length > 0}
          emptyText={t('screens.picking.pool.emptyGroups')}
        >
          {loading && groups.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {groups.map(g => {
                const isExp = expanded.has(g.groupIndex);
                const isStarting = starting === g.groupIndex;
                return (
                  <Paper key={g.groupIndex} variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {t('screens.picking.pool.groupLabel', { n: g.groupIndex + 1 })}
                      </Typography>
                      {g.groupReason && (
                        <Chip label={g.groupReason} color="secondary" variant="outlined" size="small" sx={{ height: 22 }} />
                      )}
                      {g.dominantCategoryName && (
                        <Tooltip title={(g.categoryCount ?? 1) > 1
                          ? t('screens.picking.pool.dominantCategoryTooltip', { count: g.categoryCount })
                          : t('screens.picking.pool.categoryTooltip')}>
                          <Chip
                            icon={<CategoryIcon />}
                            label={(g.categoryCount ?? 1) > 1
                              ? `${g.dominantCategoryName} +${(g.categoryCount ?? 1) - 1}`
                              : g.dominantCategoryName}
                            color="info"
                            variant="outlined"
                            size="small"
                            sx={{ height: 22 }}
                          />
                        </Tooltip>
                      )}
                      {g.hasUrgentOrders && (
                        <Tooltip title={t('screens.picking.pool.urgentContains')}>
                          <Chip icon={<BoltIcon />} label={t('screens.picking.pool.urgent')} color="error" size="small" sx={{ height: 22 }} />
                        </Tooltip>
                      )}
                      <Chip label={t('screens.picking.common.orderCount', { count: g.totalOrders })} size="small" variant="outlined" sx={{ height: 22 }} />
                      <Chip label={t('screens.picking.pool.itemLineCount', { count: g.totalItems })} size="small" variant="outlined" sx={{ height: 22 }} />
                      <Box sx={{ flex: 1 }} />
                      <Box sx={{ minWidth: 130 }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('screens.picking.pool.similarity', { score: g.cohesionScore })}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, g.cohesionScore)}
                          color={cohesionColor(g.cohesionScore)}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>
                      <IconButton size="small" onClick={() => toggleExpand(g.groupIndex)}>
                        {isExp ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                      <Button
                        variant="contained"
                        startIcon={isStarting ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                        disabled={starting !== null}
                        onClick={() => handleStartGroup(g)}
                        sx={{ minWidth: 120, height: 44 }}
                      >
                        {t('screens.picking.common.start')}
                      </Button>
                    </Box>

                    {g.topProducts.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                        {g.topProducts.map(p => (
                          <Tooltip
                            key={p.productId}
                            title={p.categoryName
                              ? t('screens.picking.pool.categoryLabel', { name: p.categoryName })
                              : t('screens.picking.pool.noCategoryInfo')}
                          >
                            <Chip
                              label={t('screens.picking.pool.productChip', {
                                name: p.productName || p.stockCode || t('screens.picking.common.productFallback'),
                                orders: p.orderCount,
                                qty: p.totalQuantity,
                              })}
                              size="small"
                              sx={{ height: 22, fontSize: '0.7rem' }}
                            />
                          </Tooltip>
                        ))}
                      </Box>
                    )}

                    <Collapse in={isExp} unmountOnExit>
                      <Divider sx={{ my: 1 }} />
                      <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                        {g.orders.map(o => (
                          <ListItem key={o.id} disablePadding sx={{ py: 0.25 }}>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" fontWeight={500}>{o.orderNumber}</Typography>
                                  <Typography variant="body2" color="text.secondary">{o.customerName}</Typography>
                                  {o.priority >= 8 && <BoltIcon color="error" sx={{ fontSize: 14 }} />}
                                </Box>
                              }
                              secondary={t('screens.picking.common.itemsCount', { count: o.itemCount })}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </Paper>
                );
              })}
            </Box>
          )}
        </WmsListSection>
      )}
    </Box>
  );
};

export default PickingPool;
